import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, ShoppingBag, Trash2, Loader2, Tag, Check } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { createCheckout, validatePromo, getProducts } from "../lib/api";

export default function CartDrawer() {
  const { items, addItem, open, setOpen, updateQty, removeItem, total, count } = useCart();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [promo, setPromo] = useState("");
  const [applied, setApplied] = useState(null);
  const [applying, setApplying] = useState(false);
  const [bumps, setBumps] = useState([]);

  useEffect(() => {
    getProducts()
      .then((all) =>
        setBumps(all.filter((p) => ["unlocked-stickers", "keymark-keychain"].includes(p.id)))
      )
      .catch(() => {});
  }, []);

  const discount = applied ? Math.round(total * applied.percent * 100) / 100 : 0;
  const finalTotal = Math.round((total - discount) * 100) / 100;

  const applyPromo = async () => {
    if (!promo.trim()) return;
    setApplying(true);
    try {
      const res = await validatePromo(promo.trim());
      if (res.valid) {
        setApplied(res);
        toast.success(`Code applied — ${res.label}`);
      } else {
        setApplied(null);
        toast.error(res.message || "Invalid code");
      }
    } catch {
      toast.error("Could not validate code");
    } finally {
      setApplying(false);
    }
  };

  const checkout = async () => {
    if (!items.length) return;
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Enter a valid email for your order confirmation.");
      return;
    }
    setLoading(true);
    try {
      const payload = items.map((i) => ({
        product_id: i.product_id,
        quantity: i.quantity,
        size: i.size,
        color: i.color,
      }));
      const { url } = await createCheckout(payload, window.location.origin, {
        email,
        name,
        promo: applied?.code,
      });
      window.location.href = url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 z-[80] h-full w-full max-w-md bg-ink border-l border-white/10 flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 h-16 border-b border-white/10">
              <div className="flex items-center gap-2 font-heading font-700">
                <ShoppingBag size={18} /> Your Cart ({count})
              </div>
              <button
                onClick={() => setOpen(false)}
                className="rounded-full border border-white/20 p-2 hover:bg-white/10"
                data-testid="cart-close-btn"
                aria-label="Close cart"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 hide-scrollbar">
              {items.length === 0 && (
                <div className="text-center text-white/50 mt-20" data-testid="cart-empty">
                  <ShoppingBag className="mx-auto mb-4 opacity-40" size={40} />
                  Your cart is empty.
                </div>
              )}
              {items.map((i) => (
                <div
                  key={i.key}
                  className="flex gap-4 rounded-2xl border border-white/10 bg-charcoal p-3"
                  data-testid={`cart-item-${i.product_id}`}
                >
                  <img src={i.image} alt={i.name} className="h-20 w-16 rounded-lg object-cover bg-black" />
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <p className="font-heading font-700 text-sm">{i.name}</p>
                      <button
                        onClick={() => removeItem(i.key)}
                        className="text-white/40 hover:text-blaze"
                        aria-label="Remove"
                        data-testid={`cart-remove-${i.product_id}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                    <p className="text-xs text-white/40 mt-0.5">
                      {[i.color, i.size].filter(Boolean).join(" · ") || "One size"}
                    </p>
                    <p className="font-mono text-blaze text-sm mt-1">${i.price.toFixed(2)}</p>
                    <div className="mt-2 inline-flex items-center gap-3 rounded-full border border-white/15 px-2 py-1">
                      <button
                        onClick={() => updateQty(i.key, -1)}
                        className="text-white/70 hover:text-white"
                        data-testid={`cart-dec-${i.product_id}`}
                        aria-label="Decrease"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="text-sm w-4 text-center" data-testid={`cart-qty-${i.product_id}`}>
                        {i.quantity}
                      </span>
                      <button
                        onClick={() => updateQty(i.key, 1)}
                        className="text-white/70 hover:text-white"
                        data-testid={`cart-inc-${i.product_id}`}
                        aria-label="Increase"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-white/10 px-6 py-5">
              {items.length > 0 &&
                bumps.filter((b) => !items.some((i) => i.product_id === b.id)).length > 0 && (
                  <div className="mb-4 rounded-xl border border-white/10 bg-charcoal p-3" data-testid="order-bump">
                    <p className="overline text-blaze mb-2">Complete the drop</p>
                    <div className="space-y-2">
                      {bumps
                        .filter((b) => !items.some((i) => i.product_id === b.id))
                        .map((b) => (
                          <div key={b.id} className="flex items-center gap-3" data-testid={`order-bump-${b.id}`}>
                            <img
                              src={b.colors[0].image}
                              alt={b.name}
                              className="h-11 w-11 rounded-md object-cover bg-black shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold truncate">{b.name}</p>
                              <p className="text-xs text-blaze font-mono">${b.price.toFixed(2)}</p>
                            </div>
                            <button
                              onClick={() =>
                                addItem({
                                  product_id: b.id,
                                  name: b.name,
                                  price: b.price,
                                  image: b.colors[0].image,
                                  color: b.colors[0].name,
                                  size: null,
                                })
                              }
                              className="rounded-full bg-white/10 hover:bg-blaze px-3 py-1.5 text-xs font-bold transition-colors"
                              data-testid={`order-bump-add-${b.id}`}
                            >
                              + Add
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

              {items.length > 0 && (
                <div className="space-y-3 mb-4">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Name (optional)"
                    className="w-full bg-transparent border-b border-white/20 py-2 px-1 text-sm text-white placeholder-white/30 focus:border-blaze focus:outline-none"
                    data-testid="cart-name-input"
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email for order confirmation"
                    className="w-full bg-transparent border-b border-white/20 py-2 px-1 text-sm text-white placeholder-white/30 focus:border-blaze focus:outline-none"
                    data-testid="cart-email-input"
                  />
                </div>
              )}
              {items.length > 0 && (
                <div className="flex gap-2 mb-4">
                  <div className="flex-1 flex items-center gap-2 border-b border-white/20 focus-within:border-blaze">
                    <Tag size={15} className="text-white/40" />
                    <input
                      type="text"
                      value={promo}
                      onChange={(e) => setPromo(e.target.value.toUpperCase())}
                      placeholder="Promo code"
                      className="w-full bg-transparent py-2 text-sm text-white placeholder-white/30 focus:outline-none uppercase"
                      data-testid="cart-promo-input"
                    />
                  </div>
                  <button
                    onClick={applyPromo}
                    disabled={applying}
                    className="rounded-full border border-white/20 px-4 py-2 text-sm font-semibold hover:bg-white/10 disabled:opacity-50"
                    data-testid="cart-promo-apply"
                  >
                    {applying ? <Loader2 size={14} className="animate-spin" /> : applied ? <Check size={14} /> : "Apply"}
                  </button>
                </div>
              )}

              <div className="space-y-1.5 mb-4">
                <div className="flex justify-between text-sm text-white/60">
                  <span>Subtotal</span>
                  <span data-testid="cart-subtotal">${total.toFixed(2)}</span>
                </div>
                {applied && (
                  <div className="flex justify-between text-sm text-blaze" data-testid="cart-discount-row">
                    <span>Discount ({applied.label})</span>
                    <span>-${discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-1">
                  <span className="text-white/60">Total</span>
                  <span className="font-heading font-800 text-xl" data-testid="cart-total">
                    ${finalTotal.toFixed(2)}
                  </span>
                </div>
              </div>
              <button
                onClick={checkout}
                disabled={loading || items.length === 0}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-blaze px-6 py-3.5 text-sm font-bold text-white transition-transform hover:scale-[1.02] hover:blaze-glow disabled:opacity-50 disabled:hover:scale-100"
                data-testid="cart-checkout-btn"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Redirecting…
                  </>
                ) : (
                  "Checkout"
                )}
              </button>
              <p className="mt-3 text-center text-xs text-white/40">Secure payment powered by Stripe</p>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
